import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MapPin, BedDouble, Bath, Maximize, Car, Building, Calendar, BadgeCheck, Clock, Phone, MessageSquare, Heart, Share2, School, Hospital, ShoppingBag, Train, Plane, Store, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { CONTACT, LOGO_URL } from "@/data/constants";

export default function PropertyDetail({ onLoginClick }) {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);
  const [enquiry, setEnquiry] = useState({ name: "", phone: "", message: "" });

  useEffect(() => {
    setEnquiry((s) => (s.message ? s : { ...s, message: t("detail.enq_msg") }));
  }, [t]);

  useEffect(() => { api.get(`/properties/${id}`).then(r => { setP(r.data); setActive(0); }).catch(() => {}); }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!enquiry.name || !/^\d{10}$/.test(enquiry.phone)) return toast.error("Enter name & valid 10-digit mobile");
    try {
      await api.post("/enquiries", { ...enquiry, property_id: p.id, subject: `Enquiry: ${p.title}` });
      toast.success("Enquiry sent — our team will contact you shortly.");
      setEnquiry({ name: "", phone: "", message: t("detail.enq_msg") });
    } catch { toast.error("Failed to send"); }
  };

  const toggleFav = async () => {
    if (!user) return onLoginClick?.();
    try { const r = await api.post(`/favorites/${p.id}`); toast.success(r.data.favorited ? "Saved" : "Removed"); } catch {}
  };

  if (!p) return <div className="max-w-[1400px] mx-auto p-10 text-center text-slate-500">{t("common.loading")}</div>;

  const purposeLabel = { sale: t("common.forSale"), rent: t("common.forRent"), lease: t("common.forLease") }[p.purpose];
  const isPending = p.status === "pending" || !p.verified;

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-6" data-testid={`property-detail-${p.id}`}>
      <div className="text-xs text-slate-500 mb-2">{t("nav.home")} / {p.locality} / {p.title}</div>
      <div className="grid lg:grid-cols-[1.6fr,1fr] gap-6">
        <div>
          <div className="rounded-2xl overflow-hidden bg-slate-100">
            <img src={p.images?.[active]} alt={p.title} className="w-full h-96 object-cover"/>
          </div>
          {p.images?.length > 1 && (
            <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar">
              {p.images.map((img, i) => (
                <button key={i} onClick={() => setActive(i)} className={`flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden border-2 ${active === i ? "border-kp-teal" : "border-transparent"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover"/>
                </button>
              ))}
            </div>
          )}

          <div className="mt-6 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="bg-kp-maroon text-white text-xs font-semibold px-2.5 py-1 rounded-full">{purposeLabel}</span>
                {p.verified ? (
                  <span className="bg-kp-teal text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1"><BadgeCheck size={12}/> {t("common.verified")}</span>
                ) : isPending ? (
                  <span className="bg-amber-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1"><Clock size={12}/> {t("common.pending")}</span>
                ) : null}
                <span className="bg-slate-100 text-slate-700 text-xs px-2.5 py-1 rounded-full">ID: {p.id.slice(0,8).toUpperCase()}</span>
              </div>
              <h1 className="font-display text-3xl font-bold text-slate-900">{p.title}</h1>
              <p className="flex items-center gap-1 text-slate-600 mt-1"><MapPin size={14}/> {p.address || `${p.locality}, ${p.city}`}</p>
              <div className="mt-3 font-display text-3xl font-bold text-kp-maroon">{p.price_label}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={toggleFav} className="w-11 h-11 rounded-full border border-slate-300 hover:bg-kp-maroon hover:text-white hover:border-kp-maroon flex items-center justify-center transition-colors" data-testid="detail-fav"><Heart size={18}/></button>
              <button onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied"); }} className="w-11 h-11 rounded-full border border-slate-300 hover:bg-kp-teal hover:text-white hover:border-kp-teal flex items-center justify-center transition-colors"><Share2 size={18}/></button>
            </div>
          </div>

          <section className="mt-6">
            <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">{t("detail.overview")}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-white border border-slate-200 rounded-xl p-5">
              {p.bedrooms != null && <div><div className="flex items-center gap-1 text-slate-500 text-xs"><BedDouble size={13}/> {t("detail.bedrooms")}</div><div className="font-semibold text-slate-900">{p.bedrooms}</div></div>}
              {p.bathrooms != null && <div><div className="flex items-center gap-1 text-slate-500 text-xs"><Bath size={13}/> {t("detail.bathrooms")}</div><div className="font-semibold text-slate-900">{p.bathrooms}</div></div>}
              <div><div className="flex items-center gap-1 text-slate-500 text-xs"><Maximize size={13}/> {t("detail.area")}</div><div className="font-semibold text-slate-900">{p.area} {t("common.sqft")}</div></div>
              {p.parking != null && <div><div className="flex items-center gap-1 text-slate-500 text-xs"><Car size={13}/> {t("detail.parking")}</div><div className="font-semibold text-slate-900">{p.parking}</div></div>}
              {p.floor != null && <div><div className="flex items-center gap-1 text-slate-500 text-xs"><Building size={13}/> {t("detail.floorLabel")}</div><div className="font-semibold text-slate-900">{p.floor}{p.total_floors ? `/${p.total_floors}` : ""}</div></div>}
              {p.age_years != null && <div><div className="flex items-center gap-1 text-slate-500 text-xs"><Calendar size={13}/> {t("detail.age")}</div><div className="font-semibold text-slate-900">{p.age_years} {t("detail.years")}</div></div>}
              {p.furnished && <div><div className="text-slate-500 text-xs">{t("post.furnishing")}</div><div className="font-semibold text-slate-900">{p.furnished}</div></div>}
              <div><div className="text-slate-500 text-xs">{t("detail.type")}</div><div className="font-semibold text-slate-900">{p.property_type}</div></div>
            </div>
          </section>

          {p.description && (
            <section className="mt-6">
              <h2 className="font-display text-xl font-semibold text-slate-900 mb-2">{t("detail.description")}</h2>
              <p className="text-slate-600 leading-relaxed whitespace-pre-line">{p.description}</p>
            </section>
          )}

          {p.amenities?.length > 0 && (
            <section className="mt-6">
              <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">{t("detail.amenities")}</h2>
              <div className="flex flex-wrap gap-2">
                {p.amenities.map((a) => <span key={a} className="bg-kp-teal-light text-kp-teal text-sm px-3 py-1.5 rounded-full">{a}</span>)}
              </div>
            </section>
          )}

          <section className="mt-6">
            <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">{t("detail.nearby")}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[{i:School,l:t("detail.schools")},{i:Hospital,l:t("detail.hospitals")},{i:ShoppingBag,l:t("detail.malls")},{i:Train,l:t("detail.railway")},{i:Plane,l:t("detail.airport")},{i:Store,l:t("detail.market")}].map((n, i) => (
                <div key={i} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg p-3"><n.i size={16} className="text-kp-teal"/> <span className="text-sm">{n.l}</span></div>
              ))}
            </div>
          </section>
        </div>

        <aside className="lg:sticky lg:top-24 self-start space-y-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="text-xs font-semibold uppercase text-kp-teal tracking-wider flex items-center gap-1"><ShieldCheck size={12}/> Listed & verified by</div>
            <div className="mt-3 flex items-center gap-3">
              <img src={LOGO_URL} alt="Krishna Property" className="w-12 h-12 rounded-full object-contain bg-kp-teal-light p-1 shrink-0"/>
              <div className="min-w-0">
                <div className="font-display font-bold text-slate-900 truncate">Krishna Property</div>
                <a href={`tel:${CONTACT.phoneRaw}`} className="text-sm text-slate-600 hover:text-kp-teal tabular-nums">{CONTACT.phone}</a>
              </div>
            </div>
            <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-1"><BadgeCheck size={11} className="text-emerald-600"/> Trusted real-estate partner · Surat</div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <a href={`tel:${CONTACT.phoneRaw}`} className="btn-primary text-center rounded-full py-2.5 text-sm font-semibold flex items-center justify-center gap-1" data-testid="btn-call"><Phone size={14}/> {t("common.callNow")}</a>
              <a href={`${CONTACT.whatsappUrl}?text=${encodeURIComponent(`Hi Krishna Property, I am interested in "${p.title}" (${window.location.href})`)}`} target="_blank" rel="noreferrer" className="btn-maroon text-center rounded-full py-2.5 text-sm font-semibold flex items-center justify-center gap-1" data-testid="btn-whatsapp"><MessageSquare size={14}/> {t("common.whatsapp")}</a>
            </div>
          </div>
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
            <h3 className="font-display font-semibold text-slate-900">{t("detail.enquiry")}</h3>
            <input required placeholder={t("detail.enq_name")} value={enquiry.name} onChange={(e) => setEnquiry({...enquiry, name: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-kp-teal" data-testid="enquiry-name"/>
            <input required placeholder={t("detail.enq_phone")} value={enquiry.phone} onChange={(e) => setEnquiry({...enquiry, phone: e.target.value.replace(/\D/g,"")})} maxLength={10} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-kp-teal" data-testid="enquiry-phone"/>
            <textarea rows={3} value={enquiry.message} onChange={(e) => setEnquiry({...enquiry, message: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-kp-teal" data-testid="enquiry-message"/>
            <Button type="submit" className="btn-primary w-full rounded-full font-semibold" data-testid="btn-submit-enquiry">{t("detail.enq_send")}</Button>
          </form>
        </aside>
      </div>
    </div>
  );
}
